import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Plus, Trash2, Save, Play, AlertTriangle, Copy, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const LABS = ['GIA', 'IGI', 'HRD', 'SGL', 'TDS'];
const SIZE_GROUPS = ['small', 'medium', 'larger', 'big'];
const SIZE_GROUP_LABELS = { small: 'Small', medium: 'Medium', larger: 'Larger', big: 'Big' };
// Representative (shape, grid_size) that maps to each size group in the backend.
const SIZE_GROUP_PROBE = {
  small:  { shape: 'Round', grid_size: '0-6' },
  medium: { shape: 'Round', grid_size: '7-12' },
  larger: { shape: 'Round', grid_size: '13-17' },
  big:    { shape: 'Round', grid_size: '18-22' },
};
const ROUNDING_OPTIONS = [
  { value: 'round_up_500', label: 'Round UP to nearest ₹500' },
  { value: 'none',         label: 'No rounding (2 decimals)' },
];

const fmtINR = (n) => (n === null || n === undefined ? '∞' : `₹${Number(n).toLocaleString('en-IN')}`);
const cloneDeep = (v) => JSON.parse(JSON.stringify(v));

const LabPricingFactors = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLab, setActiveLab] = useState('GIA');
  const [activeGroup, setActiveGroup] = useState('small');
  const [labData, setLabData] = useState({});
  const [drafts, setDrafts] = useState({});             // { lab: { slabs?, slabs_by_size_group?, rounding_mode } }
  const [effectiveFrom, setEffectiveFrom] = useState({});
  const [previewBase, setPreviewBase] = useState(100000);
  const [previewGroup, setPreviewGroup] = useState('small');
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [copySource, setCopySource] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/lab-pricing-factors');
      const map = {};
      const dr = {};
      const ef = {};
      for (const lab of res.data.labs) {
        map[lab.lab] = lab;
        dr[lab.lab] = {
          slabs: lab.slabs ? cloneDeep(lab.slabs) : null,
          slabs_by_size_group: lab.slabs_by_size_group ? cloneDeep(lab.slabs_by_size_group) : null,
          rounding_mode: lab.rounding_mode || 'round_up_500',
        };
        ef[lab.lab] = lab.effective_from || new Date().toISOString().slice(0, 10);
      }
      setLabData(map);
      setDrafts(dr);
      setEffectiveFrom(ef);
    } catch (e) {
      toast({ title: 'Failed to load lab factors', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentDraft = drafts[activeLab] || {};
  const is2D = !!currentDraft.slabs_by_size_group;
  const currentSlabs = is2D
    ? (currentDraft.slabs_by_size_group?.[activeGroup] || [])
    : (currentDraft.slabs || []);
  const currentLab = labData[activeLab] || {};

  // Baseline (last-saved) rows for the current view — used for "changed vs
  // saved" row highlighting. If the view is showing IGI/Medium we compare
  // against labData.IGI.slabs_by_size_group.medium; for flat labs we compare
  // against labData.LAB.slabs. A row is "changed" if any of its four fields
  // differ, and any row past the saved length is also considered new.
  const savedSlabs = is2D
    ? (currentLab.slabs_by_size_group?.[activeGroup] || [])
    : (currentLab.slabs || []);

  const savedRoundingMode = currentLab.rounding_mode || 'round_up_500';
  const roundingChanged = (currentDraft.rounding_mode || 'round_up_500') !== savedRoundingMode;

  const isRowChanged = (idx) => {
    const draft = currentSlabs[idx];
    const saved = savedSlabs[idx];
    if (!draft) return false;
    if (!saved) return true; // new row
    const norm = (v) => (v === '' || v === undefined ? null : (v === null ? null : Number(v)));
    return (
      norm(draft.from_base) !== norm(saved.from_base) ||
      norm(draft.to_base)   !== norm(saved.to_base)   ||
      norm(draft.factor)    !== norm(saved.factor)    ||
      draft.factor_type     !== saved.factor_type
    );
  };
  const changedRowCount = currentSlabs.reduce((n, _, i) => (isRowChanged(i) ? n + 1 : n), 0)
    + Math.max(0, savedSlabs.length - currentSlabs.length); // deleted rows

  const updateDraft = (patch) => setDrafts({ ...drafts, [activeLab]: { ...currentDraft, ...patch } });

  const writeSlabs = (nextSlabs) => {
    if (is2D) {
      updateDraft({
        slabs_by_size_group: {
          ...currentDraft.slabs_by_size_group,
          [activeGroup]: nextSlabs,
        },
      });
    } else {
      updateDraft({ slabs: nextSlabs });
    }
  };

  const setSlabField = (idx, field, value) => {
    const next = [...currentSlabs];
    next[idx] = { ...next[idx], [field]: value };
    writeSlabs(next);
  };

  const addRow = () => {
    let next = [...currentSlabs];
    if (next.length > 0 && next[next.length - 1].to_base === null) {
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, to_base: (Number(last.from_base) || 0) + 100000 };
    }
    const lastEnd = next.length > 0 ? Number(next[next.length - 1].to_base) : -1;
    next.push({
      from_base: lastEnd + 1,
      to_base: null,
      factor: is2D ? 1.0 : (activeLab === 'TDS' ? 2000 : 1.0),
      factor_type: activeLab === 'TDS' ? 'subtract' : 'multiplier',
    });
    writeSlabs(next);
  };

  const removeRow = (idx) => {
    const next = currentSlabs.filter((_, i) => i !== idx);
    if (next.length > 0 && next[next.length - 1].to_base !== null) {
      next[next.length - 1] = { ...next[next.length - 1], to_base: null };
    }
    writeSlabs(next);
  };

  const normaliseSlabs = (arr) => arr.map((s) => ({
    from_base: Number(s.from_base),
    to_base: s.to_base === null || s.to_base === '' ? null : Number(s.to_base),
    factor: Number(s.factor),
    factor_type: s.factor_type,
  }));

  // Eligible source labs for the Copy-From picker on the ACTIVE tab.
  // Only same-shape labs (flat↔flat, 2D↔2D) show up so we don't have to
  // guess how to reshape one into the other.
  const copyableSources = useMemo(() => {
    return LABS.filter((l) => {
      if (l === activeLab) return false;
      const source = labData[l];
      if (!source) return false;
      const sourceIs2D = !!source.slabs_by_size_group;
      return sourceIs2D === is2D;
    });
  }, [activeLab, is2D, labData]);

  const copyFromLab = (sourceLab) => {
    if (!sourceLab) return;
    const src = labData[sourceLab];
    if (!src) return;
    const patch = { rounding_mode: src.rounding_mode || 'round_up_500' };
    if (is2D && src.slabs_by_size_group) {
      patch.slabs_by_size_group = cloneDeep(src.slabs_by_size_group);
      patch.slabs = null;
    } else if (!is2D && src.slabs) {
      patch.slabs = cloneDeep(src.slabs);
      patch.slabs_by_size_group = null;
    } else {
      toast({ title: 'Copy failed', description: 'Shape mismatch between labs.', variant: 'destructive' });
      return;
    }
    updateDraft(patch);
    setCopySource('');
    toast({ title: `Copied ${sourceLab} → ${activeLab}`, description: 'Review the ladder and press Save to apply.' });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        rounding_mode: currentDraft.rounding_mode || 'round_up_500',
        effective_from: effectiveFrom[activeLab],
      };
      if (is2D) {
        body.slabs_by_size_group = Object.fromEntries(
          SIZE_GROUPS.map((g) => [g, normaliseSlabs(currentDraft.slabs_by_size_group?.[g] || [])]),
        );
      } else {
        body.slabs = normaliseSlabs(currentDraft.slabs || []);
      }
      await api.put(`/admin/lab-pricing-factors/${activeLab}`, body);
      toast({ title: `${activeLab} ladder saved` });
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const body = {
        base_rate: Number(previewBase),
        rounding_mode: currentDraft.rounding_mode || 'round_up_500',
      };
      if (is2D) {
        body.slabs_by_size_group = Object.fromEntries(
          SIZE_GROUPS.map((g) => [g, normaliseSlabs(currentDraft.slabs_by_size_group?.[g] || [])]),
        );
        const probe = SIZE_GROUP_PROBE[previewGroup];
        body.shape = probe.shape;
        body.grid_size = probe.grid_size;
      } else {
        body.slabs = normaliseSlabs(currentDraft.slabs || []);
      }
      const res = await api.post(`/admin/lab-pricing-factors/${activeLab}/preview`, body);
      setPreviewResult(res.data);
    } catch (e) {
      toast({ title: 'Preview failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  };

  // Summary strip (2D only): show slab count + last band per group
  const groupSummary = useMemo(() => {
    if (!is2D) return null;
    return SIZE_GROUPS.map((g) => {
      const slabs = currentDraft.slabs_by_size_group?.[g] || [];
      const last = slabs[slabs.length - 1];
      return { group: g, count: slabs.length, last };
    });
  }, [is2D, currentDraft.slabs_by_size_group]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 mt-4">Loading lab factors…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="lab-pricing-factors-page">
      <div className="bg-slate-800 text-white sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-slate-700" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">Lab Pricing Factors</h1>
              <p className="text-xs text-slate-300">Configure slab-based multipliers, subtractions & rounding per lab</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={load} className="text-white hover:bg-slate-700" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="p-3 lg:p-4 max-w-4xl mx-auto">
        <div className="mb-3 bg-emerald-50 border border-emerald-300 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="price-chart-cta">
          <div className="text-xs text-emerald-900">
            <b>Sales Chart PDF (A3 landscape)</b> — one-tap export of every colour × clarity × size × cut price using the live ladders below. Prices in ₹ &apos;000 with 1 decimal, pre-rounded.
          </div>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
            onClick={async () => {
              try {
                const res = await api.get('/admin/price-chart/download', { responseType: 'blob' });
                const blob = new Blob([res.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
                a.href = url;
                a.download = `Diamond_Price_Chart_${today}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (e) {
                toast({ title: 'Download failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
              }
            }}
            data-testid="download-price-chart-btn"
          >
            <Download className="w-3 h-3 mr-1" /> Download PDF
          </Button>
        </div>
        <Tabs value={activeLab} onValueChange={(v) => { setActiveLab(v); setActiveGroup('small'); setPreviewResult(null); setCopySource(''); }}>
          <TabsList className="grid grid-cols-5 mb-3">
            {LABS.map((l) => (
              <TabsTrigger key={l} value={l} data-testid={`tab-${l}`}>{l}</TabsTrigger>
            ))}
          </TabsList>

          {LABS.map((lab) => (
            <TabsContent key={lab} value={lab}>
              {activeLab === lab && (
                <>
                  {/* Lab-level header: version, rounding badge, copy-from */}
                  <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-gray-500">
                      v{currentLab.version || 0} · updated by <b>{currentLab.updated_by || '—'}</b>
                    </span>
                    <span className="ml-auto flex items-center gap-2 flex-wrap">
                      {copyableSources.length > 0 && (
                        <span className="flex items-center gap-1" data-testid={`copy-from-wrapper-${lab}`}>
                          <Label className="text-xs text-slate-600 whitespace-nowrap flex items-center gap-1">
                            <Copy className="w-3 h-3" /> Copy from:
                          </Label>
                          <select
                            value={copySource}
                            onChange={(e) => setCopySource(e.target.value)}
                            className="h-8 text-xs rounded border border-gray-300 px-1"
                            data-testid={`copy-from-${lab}`}
                          >
                            <option value="">— pick lab —</option>
                            {copyableSources.map((l) => (
                              <option key={l} value={l}>{l} (v{labData[l]?.version || 0})</option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            disabled={!copySource}
                            onClick={() => copyFromLab(copySource)}
                            data-testid={`copy-from-apply-${lab}`}
                          >
                            Copy
                          </Button>
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Label className="text-xs text-slate-600 whitespace-nowrap">Rounding:</Label>
                        <select
                          value={currentDraft.rounding_mode || 'round_up_500'}
                          onChange={(e) => updateDraft({ rounding_mode: e.target.value })}
                          className="h-8 text-xs rounded border border-gray-300 px-1"
                          data-testid={`rounding-mode-${lab}`}
                        >
                          {ROUNDING_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </span>
                    </span>
                  </div>

                  {/* 2D summary strip */}
                  {is2D && groupSummary && (
                    <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`group-summary-${lab}`}>
                      {groupSummary.map((s) => (
                        <button
                          key={s.group}
                          type="button"
                          onClick={() => setActiveGroup(s.group)}
                          className={`text-left rounded-lg border p-2 transition ${activeGroup === s.group ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-slate-400'}`}
                          data-testid={`group-card-${lab}-${s.group}`}
                        >
                          <div className="text-xs font-semibold text-slate-700">{SIZE_GROUP_LABELS[s.group]}</div>
                          <div className="text-[11px] text-gray-500">{s.count} slab{s.count === 1 ? '' : 's'}</div>
                          {s.last && (
                            <div className="text-[11px] text-gray-600 mt-0.5">
                              last: {s.last.factor_type === 'subtract' ? '−' : '×'}
                              <b>{s.last.factor}</b>
                              {' '}from {fmtINR(s.last.from_base)}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Size-group sub-tabs for 2D labs */}
                  {is2D && (
                    <Tabs value={activeGroup} onValueChange={setActiveGroup} className="mb-3">
                      <TabsList className="grid grid-cols-4">
                        {SIZE_GROUPS.map((g) => (
                          <TabsTrigger key={g} value={g} data-testid={`group-tab-${lab}-${g}`}>{SIZE_GROUP_LABELS[g]}</TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>
                  )}

                  {/* Slabs table */}
                  <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid={`slabs-table-${lab}`}>
                    <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        {lab} {is2D ? `— ${SIZE_GROUP_LABELS[activeGroup]}` : ''} Slabs
                        {(changedRowCount > 0 || roundingChanged) && (
                          <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium border border-amber-300" data-testid={`unsaved-chip-${lab}`}>
                            {changedRowCount > 0 && `${changedRowCount} row${changedRowCount === 1 ? '' : 's'} changed`}
                            {changedRowCount > 0 && roundingChanged && ' · '}
                            {roundingChanged && 'rounding changed'}
                          </span>
                        )}
                      </div>
                      <Button size="sm" variant="outline" onClick={addRow} data-testid={`add-slab-${lab}`}>
                        <Plus className="w-3 h-3 mr-1" /> Add Slab
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="text-left px-2 py-2">From (base ₹/ct)</th>
                            <th className="text-left px-2 py-2">To (leave blank for ∞)</th>
                            <th className="text-left px-2 py-2">Factor</th>
                            <th className="text-left px-2 py-2">Type</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentSlabs.map((s, i) => {
                            const changed = isRowChanged(i);
                            return (
                            <tr key={i} className={`border-t transition-colors ${changed ? 'bg-amber-50' : ''}`} data-testid={`slab-row-${lab}-${i}`} data-changed={changed ? '1' : '0'}>
                              <td className="px-2 py-1 relative">
                                {changed && <span className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400" aria-hidden="true" />}
                                <Input type="number" value={s.from_base} onChange={(e) => setSlabField(i, 'from_base', e.target.value)} className="h-8 text-xs" data-testid={`from-base-${lab}-${i}`} />
                              </td>
                              <td className="px-2 py-1">
                                <Input
                                  type="number"
                                  value={s.to_base === null ? '' : s.to_base}
                                  placeholder="∞"
                                  onChange={(e) => setSlabField(i, 'to_base', e.target.value === '' ? null : e.target.value)}
                                  className="h-8 text-xs"
                                  data-testid={`to-base-${lab}-${i}`}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Input type="number" step="0.00001" value={s.factor} onChange={(e) => setSlabField(i, 'factor', e.target.value)} className="h-8 text-xs" data-testid={`factor-${lab}-${i}`} />
                              </td>
                              <td className="px-2 py-1">
                                <select
                                  value={s.factor_type}
                                  onChange={(e) => setSlabField(i, 'factor_type', e.target.value)}
                                  className="h-8 text-xs rounded border border-gray-300 px-1 w-full"
                                  data-testid={`factor-type-${lab}-${i}`}
                                >
                                  <option value="multiplier">× multiplier</option>
                                  <option value="subtract">− subtract</option>
                                </select>
                              </td>
                              <td className="px-2 py-1">
                                <Button size="icon" variant="ghost" onClick={() => removeRow(i)} className="h-7 w-7" data-testid={`remove-slab-${lab}-${i}`}>
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </Button>
                              </td>
                            </tr>
                            );
                          })}
                          {currentSlabs.length === 0 && (
                            <tr><td colSpan={5} className="text-center text-gray-400 py-4 text-xs">No slabs yet — click <b>Add Slab</b> to start.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-3 py-2 border-t bg-slate-50 flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Label className="text-xs text-slate-600 whitespace-nowrap">Effective from:</Label>
                        <Input type="date" value={effectiveFrom[activeLab] || ''} onChange={(e) => setEffectiveFrom({ ...effectiveFrom, [activeLab]: e.target.value })} className="h-8 text-xs w-40" data-testid={`effective-from-${lab}`} />
                      </div>
                      <div className="flex-1"></div>
                      <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`save-${lab}`}>
                        <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving…' : (is2D ? 'Save All Groups' : 'Save Ladder')}
                      </Button>
                    </div>
                  </div>

                  {/* Preview card */}
                  <div className="bg-white rounded-xl border shadow-sm p-3 mt-3" data-testid={`preview-card-${lab}`}>
                    <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Play className="w-4 h-4" /> Preview a Base Price
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Enter a base ₹/ct — see which slab hits and the final ₹/ct (post-factor, post-rounding). Uses the <b>unsaved</b> draft above.
                    </p>
                    <div className="mt-2 flex items-end gap-2 flex-wrap">
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs text-slate-600">Base ₹/ct</Label>
                        <Input type="number" value={previewBase} onChange={(e) => setPreviewBase(e.target.value)} className="h-8 text-xs w-32" data-testid={`preview-base-${lab}`} />
                      </div>
                      {is2D && (
                        <div className="flex flex-col gap-1">
                          <Label className="text-xs text-slate-600">Size Group</Label>
                          <select
                            value={previewGroup}
                            onChange={(e) => setPreviewGroup(e.target.value)}
                            className="h-8 text-xs rounded border border-gray-300 px-1"
                            data-testid={`preview-group-${lab}`}
                          >
                            {SIZE_GROUPS.map((g) => (
                              <option key={g} value={g}>{SIZE_GROUP_LABELS[g]}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <Button size="sm" onClick={runPreview} disabled={previewing} data-testid={`run-preview-${lab}`}>
                        {previewing ? 'Calculating…' : 'Run Preview'}
                      </Button>
                    </div>
                    {previewResult && (
                      <div className="mt-3 p-2 bg-slate-50 rounded border text-xs space-y-1" data-testid={`preview-result-${lab}`}>
                        <div>Base: <b>{fmtINR(previewResult.base_rate)}</b></div>
                        <div>
                          Slab used: <b>{fmtINR(previewResult.slab_used?.from_base)} — {fmtINR(previewResult.slab_used?.to_base)}</b>
                          {' '}· {previewResult.slab_used?.factor_type} <b>{previewResult.slab_used?.factor}</b>
                        </div>
                        {previewResult.final_raw !== undefined && previewResult.final_raw !== previewResult.final_rate && (
                          <div className="text-gray-500">Pre-rounding: {fmtINR(previewResult.final_raw)}</div>
                        )}
                        <div>Adjustment: <b className={previewResult.adjustment < 0 ? 'text-red-600' : 'text-emerald-600'}>{previewResult.adjustment >= 0 ? '+' : ''}{fmtINR(previewResult.adjustment)}</b></div>
                        <div className="text-sm">Final: <b className="text-emerald-700">{fmtINR(previewResult.final_rate)}</b> <span className="text-[10px] text-gray-500">({previewResult.rounding_mode})</span></div>
                      </div>
                    )}
                  </div>

                  {/* Audit trail */}
                  {(currentLab.audit_log || []).length > 0 && (
                    <details className="bg-white rounded-xl border shadow-sm mt-3" data-testid={`audit-${lab}`}>
                      <summary className="px-3 py-2 text-sm font-semibold text-slate-700 cursor-pointer">
                        Audit Trail ({(currentLab.audit_log || []).length})
                      </summary>
                      <div className="px-3 py-2 max-h-60 overflow-y-auto text-xs space-y-1">
                        {[...(currentLab.audit_log || [])].reverse().map((e, i) => (
                          <div key={i} className="border-b pb-1 last:border-b-0">
                            <div className="text-gray-500">{new Date(e.ts).toLocaleString('en-IN')} · <span className="font-semibold">{e.user_email}</span> · {e.action}</div>
                            <div className="text-gray-600">{e.note}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2" data-testid="safety-note">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800">
            <b>Slab rules:</b> Within each ladder (or size group), bands must be contiguous (row 1 ends at ₹50,000 → row 2 must start at ₹50,001). The last row can leave <b>To</b> blank to mean ∞. For IGI/HRD/SGL you must save ALL four size groups together — the whole 2-D ladder is versioned in one shot. Old estimates are never re-priced.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabPricingFactors;
